import React from "react";
import PropTypes from "prop-types";
import Button from "shared/Button";
import styles from "./ReasonModal.module.css";

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

// Remediation modal used for both bulk refunds and bulk cancels of order
// lines. In refund mode each selected line gets a quantity selector (capped at
// the still-refundable quantity) and the refund total is calculated
// automatically — the rep never types a dollar amount. In cancel mode the
// lines are listed read-only. A reason is always required for the audit trail.
export default function ReasonModal({
  open,
  kind,
  items,
  title,
  description,
  submitLabel,
  onCancel,
  onSubmit,
  busy,
  shipping,
  otherRemaining,
}) {
  const isRefund = kind === "refund";

  const lines = React.useMemo(
    () =>
      (items || []).map((item) => {
        const totalQty = Number(item.qty) || 1;
        const refundedQty = Number(item.refundedQty) || 0;
        const remaining = Math.max(0, totalQty - refundedQty);
        return { ...item, totalQty, refundedQty, remaining };
      }),
    [items]
  );

  // Per-line refund quantities keyed by item id, defaulting to the full
  // remaining quantity for each line.
  const [quantities, setQuantities] = React.useState({});
  const [reason, setReason] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setReason("");
    const init = {};
    lines.forEach((line) => {
      init[line.id] = line.remaining;
    });
    setQuantities(init);
  }, [open, lines]);

  // Close on Escape for parity with the backdrop click. Skip while a submit is
  // in flight so ESC can't cancel the modal mid-request.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const setLineQty = (id, value, max) => {
    const clamped = Math.min(Math.max(0, Math.floor(Number(value) || 0)), max);
    setQuantities((prev) => ({ ...prev, [id]: clamped }));
  };

  const refundLines = lines.map((line) => {
    const quantity = isRefund
      ? Number.isFinite(quantities[line.id])
        ? quantities[line.id]
        : line.remaining
      : line.totalQty;
    // `price` is the pre-tax unit the IBO paid (the PM price for members, not
    // `retailPrice`). Tax is carried separately as `taxAmount` for the WHOLE
    // line. Mirroring NetSuite, a refund returns the item price AND its share of
    // that tax. Tax is apportioned cumulatively (tax on everything refunded so
    // far minus what earlier refunds already returned) so a line refunded in
    // several partial steps returns exactly the tax charged, to the cent.
    const unitNet = Number(line.price) || 0;
    const lineTax = Number(line.taxAmount);
    const taxUpTo = (units) =>
      Number.isFinite(lineTax) && lineTax > 0 && line.totalQty > 0
        ? Math.round((lineTax * 100 * units) / line.totalQty) / 100
        : 0;
    const net = Number((unitNet * quantity).toFixed(2));
    const tax = isRefund
      ? Number(
          (
            taxUpTo(line.refundedQty + quantity) - taxUpTo(line.refundedQty)
          ).toFixed(2)
        )
      : 0;
    const amount = isRefund ? Number((net + tax).toFixed(2)) : undefined;
    return { id: line.id, name: line.name, quantity, net, tax, amount };
  });

  // Shipping (and its tax) comes back with the last items on the order: only
  // when this refund leaves nothing else unrefunded, and only once.
  const shippingNet = Number(shipping?.amount) || 0;
  const shippingTax = Number(shipping?.tax) || 0;
  const refundsShipping =
    isRefund &&
    shippingNet + shippingTax > 0 &&
    !shipping?.refunded &&
    (Number(otherRemaining) || 0) === 0 &&
    refundLines.length > 0 &&
    refundLines.every((l, i) => l.quantity === lines[i].remaining);
  const shippingRefund = refundsShipping
    ? { net: shippingNet, tax: shippingTax }
    : { net: 0, tax: 0 };

  const subtotalAmount = isRefund
    ? Number(refundLines.reduce((sum, l) => sum + (l.net || 0), 0).toFixed(2))
    : undefined;
  const taxAmount = isRefund
    ? Number(
        (
          refundLines.reduce((sum, l) => sum + (l.tax || 0), 0) +
          shippingRefund.tax
        ).toFixed(2)
      )
    : undefined;
  const totalAmount = isRefund
    ? Number(
        (
          refundLines.reduce((sum, l) => sum + (l.amount || 0), 0) +
          shippingRefund.net +
          shippingRefund.tax
        ).toFixed(2)
      )
    : undefined;

  const trimmedReason = reason.trim();
  const refundQtyTotal = refundLines.reduce(
    (sum, l) => sum + (l.quantity || 0),
    0
  );
  const canSubmit =
    !busy &&
    trimmedReason.length > 0 &&
    lines.length > 0 &&
    (!isRefund || refundQtyTotal > 0);

  // In refund mode the heading counts the UNITS being refunded, not the number
  // of selected lines — two lines at 4 and 2 read "Refunding 6 items". The
  // quantities live in this component's state, so the heading has to be derived
  // here to track the qty inputs live; the `title` prop can't.
  const heading = isRefund
    ? `Refunding ${refundQtyTotal} item${refundQtyTotal === 1 ? "" : "s"}`
    : title;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      reason: trimmedReason,
      lines: refundLines,
      subtotalAmount,
      taxAmount,
      totalAmount,
      shippingCents: Math.round(
        (shippingRefund.net + shippingRefund.tax) * 100
      ),
    });
  };

  return (
    <div className={styles.backdrop} onClick={onCancel} role="presentation">
      <form
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 className={styles.title}>{heading}</h2>
        {description && <p className={styles.description}>{description}</p>}

        <ul className={styles.itemList}>
          {lines.map((line) => (
            <li key={line.id} className={styles.itemRow}>
              <div className={styles.itemInfo}>
                <span className={styles.itemName}>{line.name}</span>
                {isRefund && (
                  <span className={styles.itemMeta}>
                    {money(line.price)} each + tax
                    {line.refundedQty > 0
                      ? ` · ${line.refundedQty} already refunded`
                      : ""}
                  </span>
                )}
              </div>

              {isRefund && line.remaining > 1 && (
                <label className={styles.qtyControl}>
                  <span className={styles.qtyLabel}>Qty</span>
                  <input
                    type="number"
                    min="0"
                    max={line.remaining}
                    step="1"
                    className={styles.qtyInput}
                    value={quantities[line.id] ?? line.remaining}
                    onChange={(e) =>
                      setLineQty(line.id, e.target.value, line.remaining)
                    }
                  />
                  <span className={styles.qtyMax}>/ {line.remaining}</span>
                </label>
              )}

              {isRefund && (
                <span className={styles.lineAmount}>
                  {money(
                    (Number(line.price) || 0) *
                      (Number.isFinite(quantities[line.id])
                        ? quantities[line.id]
                        : line.remaining)
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>

        {isRefund && (
          <div className={styles.totals}>
            <div className={styles.totalLine}>
              <span>Subtotal</span>
              <span>{money(subtotalAmount)}</span>
            </div>
            {refundsShipping && (
              <div className={styles.totalLine}>
                <span>Shipping</span>
                <span>{money(shippingRefund.net)}</span>
              </div>
            )}
            <div className={styles.totalLine}>
              <span>Tax</span>
              <span>{money(taxAmount)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Total refund</span>
              <span className={styles.totalAmount}>{money(totalAmount)}</span>
            </div>
          </div>
        )}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Reason <span className={styles.required}>*</span>
          </span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Required for the audit trail"
            autoFocus
          />
        </label>

        <div className={styles.actions}>
          <Button
            type="button"
            title="Close this dialog without making any changes."
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            title={
              isRefund
                ? "Submit the refund and record the reason to the audit trail."
                : "Cancel the selected items and record the reason to the audit trail."
            }
            disabled={!canSubmit}
          >
            {busy ? "Submitting…" : submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

ReasonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  kind: PropTypes.oneOf(["refund", "cancel"]),
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  submitLabel: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  // The order's shipping and its tax, refunded with the last items on it.
  shipping: PropTypes.shape({
    amount: PropTypes.number,
    tax: PropTypes.number,
    refunded: PropTypes.bool,
  }),
  // Units still refundable on the order's lines that aren't in this refund.
  otherRemaining: PropTypes.number,
};

ReasonModal.defaultProps = {
  kind: "refund",
  items: [],
  description: undefined,
  busy: false,
  shipping: null,
  otherRemaining: 0,
};
